/*
  Cloudinary already resizes, re-encodes and serves this logo from its CDN, and
  the `srcSet` below picks the width. Routing it through `next/image` would put
  a second optimiser in front of that for no gain, so the plain element is
  deliberate.
*/
/* eslint-disable @next/next/no-img-element */
import { limitFit } from '@cloudinary/url-gen/actions/resize';
import { CenterWrapper, Flex, Icon, Text } from 'components/ui';
import { usePwaConfig } from 'hooks/use-pwa-config';
import {
  AUTH_LOGO_SIZES,
  AUTH_LOGO_WIDTHS,
  buildResponsiveImage,
} from 'lib/cloudinary-image';
import Link from 'next/link';
import { FC } from 'react';
import { cld } from 'settings/config';
import styled, { keyframes } from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';

const Container = styled.div<{ $isPwa?: boolean }>`
  display: grid;
  grid-template-columns: minmax(400px, 20%) 1fr;
  height: 100%;
  @media screen and (max-width: ${breakpoints.medium}) {
    grid-template-rows: ${({ $isPwa }) => ($isPwa ? '1fr 1fr' : '333px 1fr')};
    grid-template-columns: 1fr;
  }
`;

const SizzleContainer = styled.div`
  display: grid;
  align-content: center;
  justify-items: center;
  padding: 84px 84px 0;
  background: linear-gradient(
      0deg,
      rgba(1, 0, 80, 0.7) 0%,
      rgba(1, 0, 80, 0.7) 100%
    ),
    linear-gradient(0deg, #010136 11.99%, rgba(1, 1, 54, 0) 50%),
    center / cover no-repeat url('/images/auth-page-background.jpg');

  @media screen and (max-width: ${breakpoints.medium}) {
    align-content: center;
    padding: 0;
  }
`;

const InfoText = styled(Text)``;

const PwaContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background-color: ${globalTheme.colors.white};
  border-right: 1px solid ${globalTheme.colors.stockbookBorder};
  text-align: center;
  gap: ${globalTheme.space[1]};

  @media screen and (max-width: ${breakpoints.medium}) {
    padding: 20px;
    padding-bottom: 0;
    gap: 0px;
    border-right: none;
    justify-content: space-between;
  }
`;

const StyledLogo = styled(Icon)`
  width: 200px;
  height: 120px;
`;

const PwaLogo = styled.img`
  max-width: 250px;
  max-height: 250px;
  margin-bottom: ${globalTheme.space[0]};
`;

const SizzleText = styled(Text)`
  display: block;
  width: 100%;
  margin: 115px 0 0;

  @media screen and (max-width: ${breakpoints.medium}) {
    display: none;
  }
`;

const PerkList = styled.ul`
  display: grid;
  gap: ${globalTheme.space[2]};
  margin: 80px 0 0;

  @media screen and (max-width: ${breakpoints.medium}) {
    margin: 40px 0 0;
  }
`;

const PerkItem = styled.li`
  display: grid;
  grid-template-columns: auto 1fr;
  gap: ${globalTheme.space[0]};
  align-items: center;
`;

const ContentContainer = styled.div<{ $isPwa?: boolean }>`
  padding: 0 ${globalTheme.space[1]};
  background-color: ${({ $isPwa }) =>
    $isPwa ? globalTheme.colors.white : globalTheme.colors.neutral};
`;

const PwaTitle = styled(Text)``;

const StyledCenterWrapper = styled(CenterWrapper)`
  @media screen and (max-width: ${breakpoints.medium}) {
    align-items: flex-start;
  }
`;

// style the Link
const StyledLink = styled(Link)`
  text-decoration: none;
  font-weight: bold;
  font-size: ${globalTheme.space[1]};
  color: ${globalTheme.colors.darkGrey};
`;

const SkeletonPulse = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 1;
  }
`;

const SkeletonBox = styled.div<{
  width?: string;
  height?: string;
  mb?: string;
}>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '100%'};
  margin-bottom: ${({ mb }) => mb || '0'};
  background-color: ${globalTheme.colors.lightGray};
  border-radius: 4px;
  animation: ${SkeletonPulse} 1.5s ease-in-out infinite;
`;

const AuthSkeleton: FC = () => {
  return (
    <PwaContainer>
      <Flex direction='column' alignItems='center'>
        {/* Logo placeholder */}
        <SkeletonBox width='200px' height='200px' mb={globalTheme.space[4]} />
        {/* Title placeholder */}
        <SkeletonBox width='250px' height='40px' mb={globalTheme.space[4]} />
        {/* Powered by placeholder */}
        <SkeletonBox width='150px' height='20px' mb={globalTheme.space[6]} />
        {/* Info text placeholder */}
        <SkeletonBox width='300px' height='30px' />
      </Flex>
    </PwaContainer>
  );
};

export const AuthPageLayout: FC = ({ children }) => {
  const { config, isLoading } = usePwaConfig();

  return (
    <Container $isPwa={!!config?.org_id}>
      {isLoading ? (
        <AuthSkeleton />
      ) : config?.org_id ? (
        <PwaContainer>
          {config.logo_public_id ? (
            <img
              {...buildResponsiveImage(
                (width) =>
                  cld
                    .image(config?.logo_public_id)
                    .format('auto')
                    .quality('auto')
                    .resize(limitFit().width(width)),
                AUTH_LOGO_WIDTHS
              )}
              sizes={AUTH_LOGO_SIZES}
              style={{
                maxWidth: '80vw',
                maxHeight: '250px',
                marginBottom: globalTheme.space[0],
              }}
              alt={`${config.name} Logo`}
            />
          ) : (
            <PwaLogo
              src={config.logo}
              alt={`${config.name} Logo`}
              width={200}
              height={200}
            />
          )}
          <PwaTitle fontSize={6} fontWeight='bold' lineHeight='heading'>
            {config.name} Stockbook
          </PwaTitle>
          <Flex
            alignItems='center'
            direction='column'
            justifyContent='center'
            padding={0}
          >
            <Text fontSize={2} color='darkGrey' maxWidth={'max-content'}>
              Powered by{' '}
            </Text>
            <Flex alignItems='center' justifyContent='center' gap={0}>
              <Icon component='AgoraLogo' dontFill size={2} />
              <StyledLink
                href='https://agoralivestock.com.au/'
                target='_blank'
                rel='noopener noreferrer'
              >
                Agora Livestock
              </StyledLink>
            </Flex>
          </Flex>
          <InfoText fontSize={2} lineHeight='heading'>
            Log in using your Agora Livestock credentials
          </InfoText>
        </PwaContainer>
      ) : (
        <SizzleContainer>
          <Link href='/'>
            <StyledLogo component='AgoraLogoWithText' dontFill />
          </Link>
          <SizzleText color='pink' fontSize={7} textAlign='center'>
            Build a trusted network & increase your market access
          </SizzleText>
          <PerkList>
            <PerkItem>
              <Icon component='CheckCircleIcon' color='white' />
              <Text color='white' fontWeight={'light'}>
                Create and manage your prices
              </Text>
            </PerkItem>
            <PerkItem>
              <Icon component='CheckCircleIcon' color='white' />
              <Text color='white' fontWeight={'light'}>
                Control who sees your prices
              </Text>
            </PerkItem>
            <PerkItem>
              <Icon component='CheckCircleIcon' color='white' />
              <Text color='white' fontWeight={'light'}>
                Send and receive offers
              </Text>
            </PerkItem>
            <PerkItem>
              <Icon component='CheckCircleIcon' color='white' />
              <Text color='white' fontWeight={'light'}>
                Get signed offers from sellers
              </Text>
            </PerkItem>
          </PerkList>
        </SizzleContainer>
      )}
      <ContentContainer $isPwa={!!config?.org_id}>
        <StyledCenterWrapper>{children}</StyledCenterWrapper>
      </ContentContainer>
    </Container>
  );
};
